import { create } from 'zustand';
import type { AuthenticatedUser } from './types';

/**
 * Auth store — minimal Zustand slice holding the currently authenticated
 * user and an "auth check completed" flag.
 *
 * Why both a store AND react-query: react-query owns the network
 * lifecycle (refetch, cache, retries) for `GET /users/me`; the store
 * is the synchronous source of truth that the rest of the app reads
 * (Layout sidebar, ProtectedRoute, role guards) without depending on
 * a hook with loading states.
 *
 * The two are kept in sync from `useCurrentUser` (success → setUser,
 * 401 → clearUser).
 */
interface AuthState {
  user: AuthenticatedUser | null;
  /** True once the initial /users/me call has completed (success or fail). */
  bootstrapped: boolean;
  setUser: (user: AuthenticatedUser | null) => void;
  markBootstrapped: () => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  bootstrapped: false,
  setUser: (user) => set({ user, bootstrapped: true }),
  markBootstrapped: () => set({ bootstrapped: true }),
  clear: () => set({ user: null, bootstrapped: true }),
}));
